import { assetUrl } from './assets';
import { t } from './i18n';

export interface HomeVideoController {
  setActive(active: boolean): void;
  translate(): void;
}

export function initHomeVideo(root: HTMLElement): HomeVideoController {
  const video = root.querySelector<HTMLVideoElement>('video')!;
  const poster = root.querySelector<HTMLImageElement>('.home-video-poster')!;
  const button = root.querySelector<HTMLButtonElement>('[data-preview-toggle]')!;
  const status = root.querySelector<HTMLElement>('[data-preview-status]')!;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  let active = false, visible = false, foreground = document.hasFocus(), pageHidden = false;
  let userPaused = false, userPlayed = false, attached = false, failed = false;
  let pending = false, request = 0;

  video.muted = video.defaultMuted = true;
  video.loop = true;
  video.disablePictureInPicture = true;

  function translate() {
    const label = t(pending || !video.paused ? 'previewPause' : 'previewPlay');
    button.setAttribute('aria-label', label); button.title = label;
    button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' +
      (pending || !video.paused ? 'M6 4h4v16H6zm8 0h4v16h-4z' : 'M7 4v16l13-8z') + '"/></svg>';
    video.setAttribute('aria-label', t('previewPoster')); poster.alt = t('previewPoster');
    status.textContent = failed ? t('previewUnavailable') : '';
    button.hidden = failed;
    root.setAttribute('aria-busy', String(pending));
  }

  function permitted() {
    return active && visible && foreground && !document.hidden && !pageHidden && !failed &&
      !userPaused && (!motion.matches || userPlayed);
  }

  function stop() {
    ++request; pending = false; video.pause(); translate();
  }

  function reconcile() {
    if (motion.matches && !userPlayed) root.classList.remove('has-video');
    if (!permitted()) { stop(); return; }
    if (pending || !video.paused) return;
    // Attach the media only when it can actually play. Unity remains independent.
    if (!attached) {
      const source = video.dataset.src;
      if (!source) { failed = true; translate(); return; }
      attached = true; video.src = assetUrl(source); video.load();
    }
    const token = ++request; pending = true; translate();
    video.play().then(() => {
      if (token !== request) { if (!permitted()) video.pause(); return; }
      pending = false;
      if (!permitted()) video.pause();
      translate();
    }).catch((error: unknown) => {
      if (token !== request) return;
      pending = false;
      // Browser autoplay denial leaves an explicit play action and the poster.
      if (error instanceof DOMException && error.name === 'NotAllowedError') userPaused = true;
      else if (error instanceof DOMException && error.name === 'AbortError') { translate(); return; }
      else { failed = true; root.classList.remove('has-video'); }
      translate();
    });
  }

  button.addEventListener('click', () => {
    if (pending || !video.paused) userPaused = true;
    else { userPaused = false; userPlayed = true; foreground = true; }
    reconcile();
  });
  video.addEventListener('playing', () => {
    if (!permitted()) { stop(); return; }
    root.classList.add('has-video'); translate();
  });
  video.addEventListener('pause', translate);
  video.addEventListener('error', () => { failed = true; root.classList.remove('has-video'); stop(); });

  const observer = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .15; reconcile();
  }, { threshold: .15 });
  observer.observe(root.querySelector('.home-video-frame')!);
  motion.addEventListener('change', () => { userPlayed = false; reconcile(); });
  document.addEventListener('visibilitychange', reconcile);
  window.addEventListener('blur', () => { foreground = false; reconcile(); });
  window.addEventListener('focus', () => { foreground = true; reconcile(); });
  window.addEventListener('pagehide', () => { pageHidden = true; stop(); });
  window.addEventListener('pageshow', () => { pageHidden = false; foreground = document.hasFocus(); reconcile(); });
  translate();
  return { setActive(value) { active = value; reconcile(); }, translate };
}
